import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDocument } from '@/hooks/useDocument';
import MHeader from '@/components/ui/organisms/MHeader';
import MInfoCard from '@/components/ui/atoms/MInfoCard';
import { IconAlertSquareRoundedFilled, IconEdit, IconTrash, IconLoader, IconSquareRoundedCheckFilled } from '@tabler/icons-react';
import styles from './document.module.css';
import { getMemberInfo, useMembers } from '@/hooks/useMembers';
import MLoader from '@/components/ui/atoms/MLoader';
import { useToast } from '@/components/ui/atoms/MToastProvider';
import MSpacer from '@/components/ui/atoms/MSpacer';
import MEmptyCard from '@/components/ui/organisms/MEmptyCard';
import MPill from '@/components/ui/atoms/MPill';
import { formatCommentDate } from '@/utils/date-utils';
import { FileCategory, fileCategoryLabels, DocumentStatus } from '@/types/document.dto';
import UncategorizedDrawer from './UncategorizedDrawer';
import DocumentDrawer from './DocumentDrawer';
import DocumentCommentDrawer from './DocumentCommentDrawer';
import MDialog from '@/components/ui/atoms/MDialog';
import { getUploadProgressDocIds, clearUploadProgressDocs, scheduleUploadProgressCleanup, checkAndCleanupIfExpired, cancelScheduledCleanup, addUploadProgressDocs } from '@/utils/upload-progress-storage';
import { trackUserDataLoaded } from '@/utils/gtm';
import { ANALYTICS_SCREEN_TYPES, ANALYTICS_SCREEN_NAMES } from '@/constants/analytics.constants';

const UploadProgress: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id || '';
  const { fetchDocumentsByIds, fetchProcessingDocuments, updateUncategorizedDocument, updateDocumentFlag, deleteDocument, updateDocumentNotes } = useDocument(memberId);
  const { getManagedMembers } = useMembers({ autoFetch: false });

  // Map of document ID to document data
  const [docsMap, setDocsMap] = useState<Map<string, any>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [membersMap, setMembersMap] = useState<Map<string, any>>(new Map());
  const [selectedDocument, setSelectedDocument] = useState<any | null>(null);
  const [uncategorizedDrawerOpen, setUncategorizedDrawerOpen] = useState(false);
  const [documentDrawerOpen, setDocumentDrawerOpen] = useState(false);
  const [commentDrawerOpen, setCommentDrawerOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Processing banner state
  const [processingCounts, setProcessingCounts] = useState<{ pending: number; total: number }>({ pending: 0, total: 0 });
  const initialProcessingTotal = useRef<number>(0);

  // polling reference
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  // cleanup timer reference
  const cleanupTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Track if we've already sent the tracking event
  const hasTrackedRef = useRef(false);

  // Calculate uncategorized count from tracked documents
  const uncategorizedCount = Array.from(docsMap.values()).filter(d => d.status === DocumentStatus.Uncategorized).length;
  
  // Calculate processing counts from tracked documents
  const processingCount = Array.from(docsMap.values()).filter(d => d.status === DocumentStatus.Processing).length;
  const totalTrackedDocs = docsMap.size;
  
  // Derive an increasing "current uploading" index for banner text
  const processedSoFar = totalTrackedDocs - processingCount;
  const currentUploadingIndex = totalTrackedDocs > 0
    ? Math.min(processedSoFar + 1, totalTrackedDocs)
    : 0;

  // Initial fetch: get tracked document IDs from localStorage and fetch their data
  useEffect(() => {
    let mounted = true;
    // Check if expired and clean up if needed
    checkAndCleanupIfExpired();

    // Fetch managed members for name lookup
    const fetchMembers = async () => {
      try {
        const members = await getManagedMembers();
        const map = new Map();
        members.forEach((member: any) => {
          map.set(member.id, member);
        });
        if (mounted) setMembersMap(map);
      } catch (err) {
        console.error('Error fetching members:', err);
      }
    };

    const fetchInitial = async () => {
      setIsLoading(true);
      try {
        // Check if cleanup time has expired and clear if needed
        const wasCleared = checkAndCleanupIfExpired();
        if (wasCleared) {
          if (mounted) setIsLoading(false);
          return;
        }
        
        // Get document IDs from localStorage
        let ids = getUploadProgressDocIds();
        
        // Fetch all processing documents to catch email/WhatsApp uploads
        try {
          const processingRes = await fetchProcessingDocuments({
            filters: {
              status: { operation: 'EQUAL', value: DocumentStatus.Processing, type: 'NUMBER' }
            },
            pagination: { page: 1, limit: 100 }
          }) as any;
          
          const processingDocs = processingRes?.data || [];
          const processingIds = processingDocs.map((doc: any) => doc._id || doc.id).filter(Boolean);
          
          // Find new processing documents not in localStorage
          const existingIds = new Set(ids);
          const newProcessingIds = processingIds.filter((id: string) => !existingIds.has(id));
          
          // Add new processing documents to localStorage
          if (newProcessingIds.length > 0) {
            addUploadProgressDocs(newProcessingIds);
            ids = getUploadProgressDocIds(); // Refresh IDs after adding
          }
        } catch (err) {
          console.error('Error fetching processing documents:', err);
        }

        if (ids.length === 0) {
          if (mounted) setIsLoading(false);
          return;
        }

        // Fetch documents by IDs from localStorage
        const res = await fetchDocumentsByIds(ids) as any;
        // Response is array directly, not wrapped in {data: []}
        const docs = Array.isArray(res) ? res : [];

        if (!mounted) return;

        // Build map of our tracked documents - only real data from API
        const newMap = new Map<string, any>();
        docs.forEach((doc: any) => {
          if (doc && doc._id) {
            newMap.set(doc._id, doc);
          }
        });
        setDocsMap(newMap);
        
        // Start polling to keep data fresh
        if (!pollingRef.current) {
          pollingRef.current = setInterval(fetchPoll, 2000);
        }
      } catch (err) {
        console.error('Initial fetch error:', err);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    // Poll function fetches documents and updates status for tracked IDs
    const fetchPoll = async () => {
      try {
        let ids = getUploadProgressDocIds();
        
        // Also check for new processing documents during polling
        try {
          const processingRes = await fetchProcessingDocuments({
            filters: {
              status: { operation: 'EQUAL', value: DocumentStatus.Processing, type: 'NUMBER' }
            },
            pagination: { page: 1, limit: 100 }
          }) as any;
          
          const processingDocs = processingRes?.data || [];
          const processingIds = processingDocs.map((doc: any) => doc._id || doc.id).filter(Boolean);
          
          // Find new processing documents not in localStorage
          const existingIds = new Set(ids);
          const newProcessingIds = processingIds.filter((id: string) => !existingIds.has(id));
          
          // Add new processing documents to localStorage
          if (newProcessingIds.length > 0) {
            addUploadProgressDocs(newProcessingIds);
            ids = getUploadProgressDocIds(); // Refresh IDs after adding
          }
        } catch (err) {
          console.error('Error checking for new processing documents:', err);
        }
        
        if (ids.length === 0) {
          // No more tracked docs, stop polling
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          return;
        }

        // Fetch documents by IDs from localStorage
        const res = await fetchDocumentsByIds(ids) as any;
        // Response is array directly, not wrapped in {data: []}
        const docs = Array.isArray(res) ? res : [];

        // Update map with latest data for tracked IDs - only real data from API
        let hasProcessingDocs = false;
        
        const newMap = new Map<string, any>();
        docs.forEach((doc: any) => {
          if (doc && doc._id) {
            newMap.set(doc._id, doc);
            if (doc.status === DocumentStatus.Processing) {
              hasProcessingDocs = true;
            }
          }
        });
        
        
        setDocsMap(newMap);
        
        // Update processing counts for banner
        const pending = Array.from(newMap.values()).filter(d => d.status === DocumentStatus.Processing).length;
        const total = newMap.size;
        if (initialProcessingTotal.current === 0 || total > initialProcessingTotal.current) {
          initialProcessingTotal.current = total;
        }
        setProcessingCounts({ pending, total: initialProcessingTotal.current });
        
        // Stop polling if all documents are done processing
        if (!hasProcessingDocs) {
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          // Schedule cleanup after 1 minute of inactivity
          if (!cleanupTimerRef.current) {
            scheduleUploadProgressCleanup();
            cleanupTimerRef.current = setTimeout(() => {
              clearUploadProgressDocs();
              cleanupTimerRef.current = null;
            }, 60 * 1000); // 1 minute
          }
        } else {
          // Cancel cleanup if processing starts again
          if (cleanupTimerRef.current) {
            clearTimeout(cleanupTimerRef.current);
            cleanupTimerRef.current = null;
            cancelScheduledCleanup();
          }
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    };

    fetchMembers();
    fetchInitial();

    return () => {
      mounted = false;
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      if (cleanupTimerRef.current) {
        clearTimeout(cleanupTimerRef.current);
        cleanupTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId]);

  // Track page load once when component mounts
  useEffect(() => {
    if (!hasTrackedRef.current) {
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.DOCUMENTS,
        screenName: ANALYTICS_SCREEN_NAMES.DOCUMENTS_ADD_PROCESSING_STATUS,
      });
      hasTrackedRef.current = true;
    }
  }, []);

  // Cleanup localStorage when component unmounts (user navigates away)
  useEffect(() => {
    // Store the current docsMap in a ref to access in cleanup
    const currentDocsMapRef = docsMap;
    
    return () => {
      // This cleanup only runs when component unmounts
      const ids = getUploadProgressDocIds();
      if (ids.length === 0) return;
      
      // Check if any documents are still processing
      const hasProcessing = Array.from(currentDocsMapRef.values()).some(doc => doc.status === DocumentStatus.Processing);
      
      // If processing, cancel any scheduled cleanup
      if (hasProcessing) {
        if (cleanupTimerRef.current) {
          clearTimeout(cleanupTimerRef.current);
          cleanupTimerRef.current = null;
        }
        cancelScheduledCleanup();
      } else {
        // If not processing and no cleanup scheduled, schedule it now
        if (!cleanupTimerRef.current) {
          scheduleUploadProgressCleanup();
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps array means cleanup only runs on unmount


  // Handle document actions
  const handleUncategorizedUpdate = async (documentId: string, data: any) => {
    try {
      const doc = docsMap.get(documentId);
      if (!doc) {
        showToast({ message: 'Document not found' });
        return;
      }
      
      // Construct update payload with proper field names
      const updatePayload: any = {
        displayName: data.displayName,
        notes: data.comment,
        category: data.fileCategory,
        memberId: data.assignedTo?.id,
        categorize: true, // Trigger status change from Uncategorized to Processed
        status: 2, // DocumentStatus.Processed
      };
      
      await updateUncategorizedDocument(doc, updatePayload);
      showToast({ message: 'Document updated successfully' });
      setUncategorizedDrawerOpen(false);
      // Refresh data for this document
      const res = await fetchDocumentsByIds([documentId]) as any;
      const docs = Array.isArray(res) ? res : [];
      if (docs.length > 0 && docs[0]) {
        setDocsMap(prev => {
          const newMap = new Map(prev);
          newMap.set(documentId, docs[0]);
          return newMap;
        });
      }
    } catch (error) {
      showToast({ message: 'Failed to update document' });
    }
  };

  const handleImportantToggle = async (checked: boolean) => {
    if (!selectedDocument) return;
    try {
      await updateDocumentFlag(selectedDocument, 'isImportant', checked);
      setSelectedDocument(doc => ({ ...doc, isImportant: checked }));
      // Refresh data for this document
      const res = await fetchDocumentsByIds([selectedDocument._id]) as any;
      const docs = Array.isArray(res) ? res : [];
      if (docs.length > 0 && docs[0]) {
        setDocsMap(prev => {
          const newMap = new Map(prev);
          newMap.set(selectedDocument._id, docs[0]);
          return newMap;
        });
      }
    } catch (error) {
      // Silent error - no toast
    }
  };

  const handlePrivateToggle = async (checked: boolean) => {
    if (!selectedDocument) return;
    try {
      await updateDocumentFlag(selectedDocument, 'isPrivate', checked);
      setSelectedDocument(doc => ({ ...doc, isPrivate: checked }));
      // Refresh data for this document
      const res = await fetchDocumentsByIds([selectedDocument._id]) as any;
      const docs = Array.isArray(res) ? res : [];
      if (docs.length > 0 && docs[0]) {
        setDocsMap(prev => {
          const newMap = new Map(prev);
          newMap.set(selectedDocument._id, docs[0]);
          return newMap;
        });
      }
    } catch (error) {
      // Silent error - no toast
    }
  };

  const handleOfflineToggle = async (checked: boolean) => {
    if (!selectedDocument) return;
    try {
      await updateDocumentFlag(selectedDocument, 'availableOffline', checked);
      setSelectedDocument(doc => ({ ...doc, availableOffline: checked }));
      // Refresh data for this document
      const res = await fetchDocumentsByIds([selectedDocument._id]) as any;
      const docs = Array.isArray(res) ? res : [];
      if (docs.length > 0 && docs[0]) {
        setDocsMap(prev => {
          const newMap = new Map(prev);
          newMap.set(selectedDocument._id, docs[0]);
          return newMap;
        });
      }
    } catch (error) {
      // Silent error - no toast
    }
  };

  const handleDocumentUpdate = async () => {
    if (!selectedDocument) return;
    try {
      // Refresh data for this document after name/owner/category update
      const res = await fetchDocumentsByIds([selectedDocument._id]) as any;
      const docs = Array.isArray(res) ? res : [];
      if (docs.length > 0 && docs[0]) {
        setDocsMap(prev => {
          const newMap = new Map(prev);
          newMap.set(selectedDocument._id, docs[0]);
          return newMap;
        });
        setSelectedDocument(docs[0]);
      }
    } catch (error) {
      console.error('Failed to refresh document data:', error);
    }
  };

  const handleDelete = async (documentId: string) => {
    try {
      const doc = docsMap.get(documentId);
      if (!doc) {
        showToast({ message: 'Document not found' });
        return;
      }
      await deleteDocument(doc);
      showToast({ message: 'Document deleted successfully' });
      setDocumentDrawerOpen(false);
      setUncategorizedDrawerOpen(false);
      // Remove from map
      setDocsMap(prev => {
        const newMap = new Map(prev);
        newMap.delete(documentId);
        return newMap;
      });
    } catch (error) {
      showToast({ message: 'Failed to delete document' });
    }
  };

  const handleNotesUpdate = async (documentId: string, notes: string) => {
    try {
      const doc = docsMap.get(documentId);
      if (!doc) {
        showToast({ message: 'Document not found' });
        return;
      }
      await updateDocumentNotes(doc, notes);
      showToast({ message: 'Notes updated successfully' });
      setCommentDrawerOpen(false);
      // Refresh data for this document
      const res = await fetchDocumentsByIds([documentId]) as any;
      const docs = Array.isArray(res) ? res : [];
      if (docs.length > 0 && docs[0]) {
        setDocsMap(prev => {
          const newMap = new Map(prev);
          newMap.set(documentId, docs[0]);
          return newMap;
        });
      }
    } catch (error) {
      showToast({ message: 'Failed to update notes' });
    }
  };



  return (
    <>
      <div style={{ paddingLeft: 24, paddingRight: 24 }}>
        <MHeader
          module="documents"
          memberInfo={memberInfo}
          title="Processing status"
          description="Your files are being processed. You can exit this screen anytime and continue using Maatra."
          learnMoreContent={
          <>View the processing status for your files from here.
          <br/> <br/> If a file is needs your action, it means we couldn’t organise it confidently and need your input on document owner or type. You can review and organise them correctly from here.</>}
          disableSticky
        />

        <MSpacer mt={24} />

        {/* Processing Documents Banner */}
        {totalTrackedDocs > 0 && (
          <div className={styles.uncategorizedPillWrapper}>
            <MPill
              icon={
                processingCount > 0 ? (
                  <IconLoader className={styles.processingSpinner} />
                ) : (
                  <IconSquareRoundedCheckFilled className={styles.processingSuccessIcon} />
                )
              }
              className={processingCount > 0 ? styles.uncategorizedPill : styles.processingSuccessPill}
              variant="banner"
              uncategorized={true}
            >
              <span className={processingCount > 0 ? styles.uncategorizedText : styles.processingSuccessText}>
                {processingCount > 0 && currentUploadingIndex > 0
                  ? `Processing ${currentUploadingIndex} of ${totalTrackedDocs} files`
                  : 'All files processed'}
              </span>
            </MPill>
          </div>
        )}

        <MSpacer mt={16} />

        {/* Uncategorized Documents Banner */}
        {/* {uncategorizedCount > 0 && (
          <div className={styles.uncategorizedPillWrapper}>
            <MPill
              icon={<IconAlertSquareRoundedFilled className={styles.uncategorizedIcon} />}
              className={styles.uncategorizedPill}
              variant="banner"
              uncategorized={true}
            >
              <span className={styles.uncategorizedText}>
                {uncategorizedCount} of {docsMap.size} files need review
              </span>
            </MPill>
          </div>
        )} */}

        <MSpacer mt={16} />

        <div style={{ marginBottom: 40 }}>
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
            <MLoader size={40} />
          </div>
        ) : docsMap.size === 0 ? (
          <MEmptyCard primaryTitle="No ongoing uploads" />
        ) : (
          <div className={styles.uploadProgressSection}>
            {Array.from(docsMap.values()).map((doc, idx) => {
              if (!doc) return null;
              const isProcessed = doc.status === DocumentStatus.Processed;
              const isReview = doc.status === DocumentStatus.Uncategorized;
              const isProcessing = doc.status === DocumentStatus.Processing;
              
              const member = membersMap.get(doc.memberId);
              const assignedName = member
                ? `${member.nickname || member.first_name}`.trim()
                : '';
              const docType = fileCategoryLabels[doc.category as FileCategory] || '';
              const dateLabel = doc.createdAt ? formatCommentDate(doc.createdAt) : '';

              const statusLabel = isProcessed
                ? 'Processed'
                : isReview
                  ? 'Review'
                  : 'Processing';

              const statusColor: any = isProcessed
                ? 'green'
                : isReview
                  ? 'yellow'
                  : 'orange';

              const handleDotsClick = () => {
                setSelectedDocument(doc);
                if (isProcessed) {
                  setDocumentDrawerOpen(true);
                }
                // For review cards, options menu will be shown via actions prop
              };

              const handleNameClick = () => {
                if (isProcessing) return;
                if (!isProcessed && !isReview) return;
                navigate('/documents/view', { state: { document: doc, backUrl: '/documents/upload-progress' } });
              };

              // Review cards show Edit and Delete options menu
              const reviewActions = isReview ? [
                {
                  id: 'edit',
                  label: 'Edit',
                  icon: <IconEdit size={20} />,
                  onClick: () => {
                    setSelectedDocument(doc);
                    setUncategorizedDrawerOpen(true);
                  },
                },
                {
                  id: 'delete',
                  label: 'Delete',
                  icon: <IconTrash size={20} />,
                  onClick: () => {
                    setSelectedDocument(doc);
                    setDeleteDialogOpen(true);
                  },
                  delete: true,
                },
              ] : undefined;

              return (
                <MInfoCard
                  key={doc._id || idx}
                  primaryLabel={doc.media?.fileName || doc.displayName || 'Document'}
                  highlightedLabel={isProcessed && assignedName && docType ? `Assigned to ${assignedName} · ${docType}` : undefined}
                  warningLabel={isReview ? (doc.ocrError || 'Could not identify document type') : undefined}
                  onPrimaryClick={(isProcessed || isReview) && doc ? handleNameClick : undefined}
                  primaryClickDisabled={isProcessing}
                  wrapLabel
                  variant={isReview ? 'actionsMenu' : 'labels'}
                  labelText={statusLabel}
                  labelColor={statusColor}
                  actions={reviewActions}
                  showOptionsMenu={isProcessed}
                  onOptionsClick={isProcessed ? handleDotsClick : undefined}
                  optionsMenuDisabled={isProcessing}
                />
              );
            })}
          </div>
        )}
        </div>
      </div>

      {/* Drawers */}
      {selectedDocument && (
        <>
          <UncategorizedDrawer
            open={uncategorizedDrawerOpen}
            onClose={() => setUncategorizedDrawerOpen(false)}
            document={selectedDocument}
            onDone={(fields) => handleUncategorizedUpdate(selectedDocument._id, fields)}
          />

          <DocumentDrawer
            open={documentDrawerOpen}
            onClose={() => setDocumentDrawerOpen(false)}
            displayName={selectedDocument.displayName || selectedDocument.media?.fileName || 'Document'}
            fileKey={selectedDocument.fileKey}
            fileName={selectedDocument.media?.fileName}
            onDelete={() => handleDelete(selectedDocument._id)}
            onCommentClick={() => {
              setDocumentDrawerOpen(false);
              setCommentDrawerOpen(true);
            }}
            commentButtonText="Add Comment"
            category={selectedDocument.category}
            document={selectedDocument}
            onUpdate={handleDocumentUpdate}
            onDocumentChange={(updatedDoc) => {
              // Update selectedDocument only (no documents array in UploadProgress)
              setSelectedDocument(prev => ({ ...prev, ...updatedDoc }));
            }}
          />

          <DocumentCommentDrawer
            open={commentDrawerOpen}
            onClose={() => setCommentDrawerOpen(false)}
            initialValue={selectedDocument.notes || ''}
            onDone={(notes) => handleNotesUpdate(selectedDocument._id, notes)}
            title="Add Comment"
          />
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <MDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title="Delete document?"
        description="This document will be permanently deleted."
        primaryCtaText="Yes"
        secondaryCtaText="No"
        onPrimaryCta={async () => {
          if (selectedDocument) {
            await handleDelete(selectedDocument._id);
            setDeleteDialogOpen(false);
          }
        }}
        onSecondaryCta={() => setDeleteDialogOpen(false)}
      />
    </>
  );
};

export default UploadProgress;